import FormMemberWrapper from "@domain/member/form-member-wrapper/form-member-wrapper";
import "./transaction-parent.scss";
import { useTransactionStepContext } from "../transaction-step.context";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useNewTransactionContext } from "../new-transaction.context";
import { LabelValue } from "@shared/models/label-value.interface";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import ParentInfo, { ParentInfoMember } from "@core/ui/parent-info/parent-info";
import { Button } from "primereact/button";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MemberFormMap } from "@core/model/member-form-map";
import ImageViewer from "@shared/ui/hook-form/image-control/image-viewer/image-viewer";
import { calculateAge } from "@shared/utility/calculage-age";
import { useForm } from "react-hook-form";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import classNames from "classnames";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { MemberFormSchema } from "@factory/form/form.rule";
import { useQyGetTransactionService } from "@factory/query/transaction-service.query";

const getAvatarCache = (): Map<string, string> => {
  const win = window as any;
  if (!win.__avatarCache) {
    win.__avatarCache = new Map<string, string>();
  }
  return win.__avatarCache;
};

interface ParentGuardianFormValues {
  parentFirstName: string;
  parentLastName: string;
  parentMobileNumber: string;
  addAsGuardian: boolean;
  guardianFirstName?: string;
  guardianLastName?: string;
  guardianMobileNumber?: string;
}

/* eslint-disable-next-line */
export interface TransactionParentProps { }

export function TransactionParent() {
  const {
    parentList,
    selectedParent,
    parentMemberRef,
    parentFormData,
    handleParentForm,
    setSelectedParent,
    handleParentTrigger,
    nextStepper,
    previousStepper,
    isCreateNewChild,
    setSelectedChildren,
    childFormData,
    childFirstList,
    selectedChildren,
    isParentListLoading,
    parentGuardianFormDraft,
    setParentGuardianFormDraft,
  } = useTransactionStepContext();
  const {
    isCreating,
    parentInfo,
    isCreateNewParent,
    setIsCreateNewParent,
    setAddNewChild,
    setIsCreating,
    setParentInfo,
    setPendingParentForm,
  } = useNewTransactionContext();

  const [isSaving] = useState(false);

  const firstChildCode = selectedChildren?.[0];

  const { data: latestServices } = useQyGetTransactionService(
    "",
    1,
    0,
    undefined,
    firstChildCode ? { member: firstChildCode } : undefined,
    !!firstChildCode && !isCreateNewChild
  );

  // Reset selected parent when the selected child changes so auto-selection can run again
  useEffect(() => {
    if (firstChildCode) {
      setSelectedParent("");
    }
  }, [firstChildCode, setSelectedParent]);

  // Derive the matched guardian from the selected children's parent_code
  const matchedGuardian = useMemo(() => {
    if (isCreateNewChild || isParentListLoading) return null;
    if (!selectedChildren || selectedChildren.length === 0) return null;
    if (!childFirstList || childFirstList.length === 0) return null;
    if (!parentList || parentList.length === 0) return null;

    const parentCodes = selectedChildren
      .map((code) => {
        const child = childFirstList.find((c) => c.value === code);
        return child?.payload?.parent_code as string | undefined;
      })
      .filter(Boolean) as string[];

    if (parentCodes.length === 0) return null;

    const uniqueCodes = Array.from(new Set(parentCodes));
    if (uniqueCodes.length !== 1) return null;

    return parentList.find((p) => p.value === uniqueCodes[0]) || null;
  }, [isCreateNewChild, isParentListLoading, selectedChildren, childFirstList, parentList]);

  // Auto-select the latest used guardian or fall back to child's parent_code when child is selected
  useEffect(() => {
    if (isCreateNewParent || selectedParent) return;

    // 1. If we have a latest transaction service for this child, try to select that guardian
    const latestGuardianCode = (latestServices?.data?.[0] as any)?.guardian;
    if (latestGuardianCode) {
      const foundGuardian = parentList.find((p) => p.value === latestGuardianCode);
      if (foundGuardian) {
        setSelectedParent(foundGuardian.value);
        return;
      }
    }

    // 2. Fallback to the child's registered parent (matchedGuardian)
    if (matchedGuardian) {
      setSelectedParent(matchedGuardian.value);
    }
  }, [isCreateNewParent, selectedParent, latestServices, parentList, matchedGuardian, setSelectedParent]);

  const ParentGuardianSchema = useMemo(() => {
    return z.object({
      parentFirstName: z.string().min(1, "Parent first name is required"),
      parentLastName: z.string().min(1, "Parent last name is required"),
      parentMobileNumber: z.string().min(1, "Parent mobile number is required"),
      addAsGuardian: z.boolean().default(true),
      guardianFirstName: z.string().optional(),
      guardianLastName: z.string().optional(),
      guardianMobileNumber: z.string().optional(),
    }).superRefine((val, ctx) => {
      if (!val.addAsGuardian && isCreateNewChild) {
        if (!val.guardianFirstName || val.guardianFirstName.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Guardian first name is required",
            path: ["guardianFirstName"],
          });
        }
        if (!val.guardianLastName || val.guardianLastName.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Guardian last name is required",
            path: ["guardianLastName"],
          });
        }
        if (!val.guardianMobileNumber || val.guardianMobileNumber.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Guardian mobile number is required",
            path: ["guardianMobileNumber"],
          });
        }
      }
    });
  }, [isCreateNewChild]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ParentGuardianFormValues>({
    resolver: zodResolver(ParentGuardianSchema),
    defaultValues: parentGuardianFormDraft,
  });

  const addAsGuardianValue = watch("addAsGuardian");
  const watchedValues = watch();

  useEffect(() => {
    setParentGuardianFormDraft(watchedValues);
  }, [watchedValues, setParentGuardianFormDraft]);

  // Auto-fill the "Create New Guardian" form when a matched guardian is found
  useEffect(() => {
    if (!isCreateNewParent || !matchedGuardian) return;
    const payload = matchedGuardian.payload;
    if (!payload) return;
    setValue("parentFirstName", payload.first_name || "");
    setValue("parentLastName", payload.last_name || "");
    setValue("parentMobileNumber", payload.mobile_number || "");
  }, [isCreateNewParent, matchedGuardian, setValue]);

  const handleNewParent = () => {
    setIsCreateNewParent(true);
    setAddNewChild(true);
  };
  const handleSelectParent = () => {
    setIsCreateNewParent(false);
  };

  const onFormSubmit = useCallback(async (values: ParentGuardianFormValues) => {
    // Build the parent form
    const parentForm: MemberFormSchema = {
      firstName: values.parentFirstName,
      lastName: values.parentLastName,
      mobileNumber: values.parentMobileNumber,
      gender: "MALE",
      age: 0,
    };

    let guardianForm: MemberFormSchema | null = null;
    if (!values.addAsGuardian) {
      guardianForm = {
        firstName: values.guardianFirstName || "",
        lastName: values.guardianLastName || "",
        mobileNumber: values.guardianMobileNumber || "",
        gender: "MALE",
        age: 0,
      };
    }

    // Defer the actual API call to when the user clicks "Book".
    // Just store the form data and move to the next step.
    setPendingParentForm({
      parentForm,
      guardianForm,
      addAsGuardian: values.addAsGuardian,
    });

    nextStepper();
  }, [
    setPendingParentForm,
    nextStepper,
  ]);

  const handleParentNext = useCallback(async () => {
    if (isSaving) return;

    if (isCreateNewParent) {
      // Validate the form, then defer API call to booking time
      await handleSubmit(onFormSubmit)();
    } else {
      // Existing guardian selected — just proceed
      nextStepper();
    }
  }, [
    isCreateNewParent,
    handleSubmit,
    onFormSubmit,
    nextStepper,
    isSaving,
  ]);

  const handleParentSelection = async (e: DropdownChangeEvent) => {
    setIsCreating(true);
    setSelectedParent(e.value);
    const filteredParent = parentList.find(
      (item) => item.value === e.value
    ) as LabelValue;

    if (!filteredParent) return;
    const parentPayload = filteredParent.payload;

    let fileBlob = undefined;
    if (parentPayload.avatar) {
      const elementFile = await viewAsset(parentPayload.avatar || "");
      fileBlob = URL.createObjectURL(elementFile);
    }

    setParentInfo({ ...parentPayload, fileBlob });
    setIsCreating(false);
  };

  const isDisabledNext = useCallback(() => {
    const isSelectingParentButNoData = !isCreateNewParent && !selectedParent;
    const isDisabled = isCreating || isSaving || isSelectingParentButNoData;
    return isDisabled;
  }, [isCreateNewParent, selectedParent, isCreating, isSaving]);

  const childrenToDisplay = useMemo(() => {
    if (isCreateNewChild) {
      return childFormData.map((child, index) => ({
        key: `new-${index}`,
        name: `${child.firstName || ""} ${child.lastName || ""}`.trim() || `Child #${index + 1}`,
        age: child.age,
        gender: child.gender,
        photo: child.photo,
      }));
    } else {
      return selectedChildren
        .map((code) => {
          const found = childFirstList.find((c) => c.value === code);
          if (!found) return null;
          return {
            key: code,
            name: found.label || "Selected Child",
            age: found.payload?.age || (found.payload?.birthday ? calculateAge(found.payload.birthday) : undefined),
            gender: found.payload?.gender,
            photo: found.payload?.avatar || found.payload?.fileBlob,
          };
        })
        .filter(Boolean) as any[];
    }
  }, [isCreateNewChild, childFormData, selectedChildren, childFirstList]);

  const [resolvedPhotos, setResolvedPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    const cache = getAvatarCache();

    // Instantly resolve already cached avatar blob URLs to prevent stale photos lingering or delayed loads.
    const initialPhotos: Record<string, string> = {};
    childrenToDisplay.forEach((child) => {
      if (!child.photo) return;
      if (typeof child.photo === "string") {
        if (child.photo.startsWith("http") || child.photo.startsWith("blob:")) {
          initialPhotos[child.key] = child.photo;
        } else if (cache.has(child.photo)) {
          initialPhotos[child.key] = cache.get(child.photo)!;
        }
      }
    });
    setResolvedPhotos(initialPhotos);

    const fetchPhotos = async () => {
      const promises = childrenToDisplay.map(async (child) => {
        if (!child.photo) return { key: child.key, url: "" };

        if (child.photo instanceof File) {
          return { key: child.key, url: URL.createObjectURL(child.photo) };
        } else if (typeof child.photo === "string") {
          if (child.photo.startsWith("http") || child.photo.startsWith("blob:")) {
            return { key: child.key, url: child.photo };
          } else {
            // Check cache
            if (cache.has(child.photo)) {
              return { key: child.key, url: cache.get(child.photo)! };
            }
            try {
              const elementFile = await viewAsset(child.photo);
              const fileBlob = URL.createObjectURL(elementFile);
              cache.set(child.photo, fileBlob);
              return { key: child.key, url: fileBlob };
            } catch (err) {
              console.error("Failed to load child avatar", err);
              return { key: child.key, url: "" };
            }
          }
        }
        return { key: child.key, url: "" };
      });

      const resolved = await Promise.all(promises);
      if (active) {
        const newResolved: Record<string, string> = {};
        resolved.forEach((item) => {
          if (item.url) {
            newResolved[item.key] = item.url;
          }
        });
        setResolvedPhotos(newResolved);
      }
    };

    fetchPhotos();
    return () => {
      active = false;
    };
  }, [childrenToDisplay]);

  return (
    <div id="TransactionParent" className="transaction-parent">

      <div className={`tp-layout${childrenToDisplay.length === 0 ? ' tp-layout--no-side' : ''}`}>

        {/* ── Left panel: child summary ───────────────────────────── */}
        {childrenToDisplay.length > 0 && (
          <aside className="tp-side">
            <p className="tp-side__label">Registering guardian for</p>
            <div className="tp-side__cards">
              {childrenToDisplay.map((child) => (
                <div key={child.key} className="tp-child-card">
                  {resolvedPhotos[child.key] ? (
                    <ImageViewer
                      image={resolvedPhotos[child.key]}
                      className="tp-child-card__avatar"
                    />
                  ) : (
                    <span className="tp-child-card__initial">
                      {child.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="tp-child-card__body">
                    <span className="tp-child-card__name">{child.name}</span>
                    <div className="tp-child-card__meta">
                      {child.age !== undefined && (
                        <span className="tp-child-card__age">{child.age} yrs</span>
                      )}
                      {child.gender && (
                        <span className={`tp-badge tp-badge--${child.gender.toLowerCase()}`}>
                          {child.gender.charAt(0).toUpperCase() + child.gender.slice(1).toLowerCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        )}

        {/* ── Right panel: guardian controls ─────────────────────── */}
        <div className="tp-main">

          {/* Mode toggle */}
          <div className="tp-mode-toggle">
            <button
              type="button"
              className={`tp-mode-btn${!isCreateNewParent ? " tp-mode-btn--active" : ""}`}
              onClick={handleSelectParent}
            >
              Select existing
            </button>
            <button
              type="button"
              className={`tp-mode-btn${isCreateNewParent ? " tp-mode-btn--active" : ""}`}
              onClick={handleNewParent}
            >
              Create new
            </button>
          </div>

          {/* Content */}
          <div className="tp-body">
            {isCreateNewParent ? (
              <div className="tp-form">
                {/* Parent fields */}
                <fieldset className="tp-fieldset">
                  <legend className="tp-legend">Parent</legend>
                  <div className="tp-field-row">
                    <div className="tp-field">
                      <label className="tp-label">First name</label>
                      <InputText
                        {...register("parentFirstName")}
                        placeholder="e.g. Maria"
                        className={classNames("tp-input", { "p-invalid": errors.parentFirstName })}
                      />
                      {errors.parentFirstName && (
                        <small className="tp-error">{errors.parentFirstName.message}</small>
                      )}
                    </div>
                    <div className="tp-field">
                      <label className="tp-label">Last name</label>
                      <InputText
                        {...register("parentLastName")}
                        placeholder="e.g. Santos"
                        className={classNames("tp-input", { "p-invalid": errors.parentLastName })}
                      />
                      {errors.parentLastName && (
                        <small className="tp-error">{errors.parentLastName.message}</small>
                      )}
                    </div>
                    <div className="tp-field">
                      <label className="tp-label">Mobile number</label>
                      <InputText
                        {...register("parentMobileNumber")}
                        placeholder="e.g. 09xx xxx xxxx"
                        className={classNames("tp-input", { "p-invalid": errors.parentMobileNumber })}
                      />
                      {errors.parentMobileNumber && (
                        <small className="tp-error">{errors.parentMobileNumber.message}</small>
                      )}
                    </div>
                  </div>
                </fieldset>

                {/* Mark-as-guardian toggle */}
                <label className="tp-checkbox-row">
                  <Checkbox
                    inputId="addAsGuardian"
                    checked={addAsGuardianValue}
                    onChange={(e) => setValue("addAsGuardian", !!e.checked)}
                  />
                  <span className="tp-checkbox-label">Mark as guardian</span>
                </label>

                {/* Guardian fields (if different) */}
                {!addAsGuardianValue && (
                  <fieldset className="tp-fieldset">
                    <legend className="tp-legend">Guardian</legend>
                    <div className="tp-field-row">
                      <div className="tp-field">
                        <label className="tp-label">First name</label>
                        <InputText
                          {...register("guardianFirstName")}
                          placeholder="e.g. Jose"
                          className={classNames("tp-input", { "p-invalid": errors.guardianFirstName })}
                        />
                        {errors.guardianFirstName && (
                          <small className="tp-error">{errors.guardianFirstName.message}</small>
                        )}
                      </div>
                      <div className="tp-field">
                        <label className="tp-label">Last name</label>
                        <InputText
                          {...register("guardianLastName")}
                          placeholder="e.g. Reyes"
                          className={classNames("tp-input", { "p-invalid": errors.guardianLastName })}
                        />
                        {errors.guardianLastName && (
                          <small className="tp-error">{errors.guardianLastName.message}</small>
                        )}
                      </div>
                      <div className="tp-field">
                        <label className="tp-label">Mobile number</label>
                        <InputText
                          {...register("guardianMobileNumber")}
                          placeholder="e.g. 09xx xxx xxxx"
                          className={classNames("tp-input", { "p-invalid": errors.guardianMobileNumber })}
                        />
                        {errors.guardianMobileNumber && (
                          <small className="tp-error">{errors.guardianMobileNumber.message}</small>
                        )}
                      </div>
                    </div>
                  </fieldset>
                )}
              </div>
            ) : (
              <div className="tp-select">
                <Dropdown
                  value={isParentListLoading ? "" : selectedParent}
                  onChange={handleParentSelection}
                  options={parentList}
                  placeholder={isParentListLoading ? "Loading…" : "Search guardian"}
                  filter
                  className="tp-dropdown"
                  virtualScrollerOptions={{ itemSize: 38 }}
                  disabled={isParentListLoading}
                  loading={isParentListLoading}
                />
                {parentInfo && (
                  <div className="tp-parent-info">
                    <ParentInfoMember model={MemberFormMap.Map(parentInfo)} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="tp-footer">
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              severity="success"
              outlined
              onClick={previousStepper}
            />
            <Button
              label="Next"
              icon="pi pi-arrow-right"
              iconPos="right"
              disabled={isDisabledNext()}
              onClick={handleParentNext}
            />
          </div>
        </div>

      </div>
    </div>
  );
}

export default TransactionParent;


